import { useState } from 'react';
import toast from 'react-hot-toast';
import { Mail } from 'lucide-react';
import { SlideOver } from '../common/SlideOver';
import { useEmailStore } from '../../store/emailStore';
import { useGmailStore } from '../../store/gmailStore';
import type { Activity } from '../../types';

interface Props {
  isOpen: boolean;
  personId: string;
  toEmail: string;
  canSend: boolean;
  onSent: (activity: Activity) => void;
  onClose: () => void;
}

export function ComposeEmailSheet({ isOpen, personId, toEmail, canSend, onSent, onClose }: Props) {
  const [to, setTo] = useState(toEmail);
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showCc, setShowCc] = useState(false);
  const { sendEmail, isSending } = useEmailStore();
  // Fresh connect (requests read+send+settings) — works whether the rep has never
  // connected, disconnected, or is connected read-only. The send-upgrade route
  // 400s without an existing ACTIVE connection, so it can't gate a first connect.
  const initiateConnect = useGmailStore((s) => s.initiateConnect);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) { toast.error('Add a subject and a message.'); return; }
    try {
      const result = await sendEmail({ personId, to, cc: cc || undefined, bcc: bcc || undefined, subject, body });
      if ('needsAuth' in result) { toast.error('Re-connect Gmail to enable sending.'); return; }
      toast.success('Email sent');
      onSent(result.activity);
      setTo(toEmail);
      setCc('');
      setBcc('');
      setSubject('');
      setBody('');
      setShowCc(false);
      onClose();
    } catch {
      toast.error('Could not send the email. Try again.');
    }
  };

  const footer = canSend ? (
    <div className="flex gap-2 justify-end">
      <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
      <button type="button" className="btn-primary" onClick={handleSend} disabled={isSending}>
        {isSending ? 'Sending…' : 'Send'}
      </button>
    </div>
  ) : undefined;

  return (
    <SlideOver isOpen={isOpen} onClose={onClose} title="New email" footer={footer}>
      {canSend ? (
        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="email-to">To</label>
            <input id="email-to" className="input" value={to} onChange={(e) => setTo(e.target.value)} />
          </div>
          {!showCc && (
            <button type="button" className="text-sm text-blast-600 dark:text-blast-400"
              onClick={() => setShowCc(true)}>Cc · Bcc</button>
          )}
          {showCc && (
            <>
              <div>
                <label className="label" htmlFor="email-cc">Cc</label>
                <input id="email-cc" className="input" value={cc} onChange={(e) => setCc(e.target.value)} />
              </div>
              <div>
                <label className="label" htmlFor="email-bcc">Bcc</label>
                <input id="email-bcc" className="input" value={bcc} onChange={(e) => setBcc(e.target.value)} />
              </div>
            </>
          )}
          <div>
            <label className="label" htmlFor="email-subject">Subject</label>
            <input id="email-subject" className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="email-body">Message</label>
            <textarea id="email-body" className="input min-h-[10rem]" value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">Your Gmail signature is added automatically.</p>
        </div>
      ) : (
        <div className="space-y-4 text-center py-6">
          <Mail className="h-8 w-8 mx-auto text-gray-400" />
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Connect your Gmail to send and auto-log email from the CRM.
          </p>
          <button type="button" className="btn-primary" onClick={() => initiateConnect()}>Connect Gmail to send</button>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            If you can't connect, your Google Workspace settings may be blocking it.
            Contact the Blast home office for assistance in getting this fixed.
          </p>
        </div>
      )}
    </SlideOver>
  );
}
